/**
 * StalePendingCleanup.jsx — admin-only card that lists pending uploads
 * older than N days (default 30) and lets the admin soft-hide them.
 *
 * Mirrors TestArtifactsCleanup's UX: server-authoritative candidate set,
 * per-row "keep" opt-out, confirm dialog, recoverable via Mongo.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles, Loader2, Eye, Trash2, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StalePendingCleanup = ({ onCleaned }) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(30);
  const [excluded, setExcluded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/admin/library/stale-pending?days=${days}`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (err) {
      toast.error(err.message || 'Could not load preview');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const toggleKeep = (content_id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(content_id)) next.delete(content_id);
      else next.add(content_id);
      return next;
    });
  };

  const target_ids = items.map((i) => i.content_id).filter((id) => !excluded.has(id));

  const hide = async () => {
    if (target_ids.length === 0) {
      toast.info('Nothing selected to hide.');
      return;
    }
    if (!window.confirm(
      `Hide ${target_ids.length} stale pending upload(s) older than ${days} days?\n\nRows stay in the database (admins still see them) but will disappear from public library views. Writers can resubmit.`
    )) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/library/stale-pending/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_ids: target_ids, days }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Hidden ${d.hidden} stale upload(s)`);
      setExcluded(new Set());
      await fetchPreview();
      onCleaned?.();
    } catch (err) {
      toast.error(err.message || 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-cyan-500/30 mb-6" data-testid="admin-stale-pending-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-cyan-400" />
            Stale pending uploads
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-300 flex items-center gap-1">
              Older than
              <input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 30)))}
                className="w-16 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                data-testid="admin-stale-days-input"
              />
              <span>days</span>
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchPreview}
              disabled={loading || busy}
              className="h-8 text-gray-300 hover:text-white"
              data-testid="admin-stale-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardTitle>
        <p className="text-xs text-gray-400 mt-1">
          Writers who start an upload and never finish leave rows sitting in <code>status=pending</code> indefinitely. This sweeps them out so your admin queue reflects real work. Hidden rows are recoverable (Mongo <code>is_hidden=false</code>), and scribes can always resubmit.
        </p>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="py-6 text-center text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Scanning…
          </div>
        ) : items.length === 0 ? (
          <div className="py-4 text-center text-emerald-300 font-semibold" data-testid="admin-stale-empty">
            <Sparkles className="w-4 h-4 inline-block mr-1" />
            No pending uploads older than {days} days. Queue is fresh.
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-300 mb-2">
              Untick anything you want to <em>keep</em>. {target_ids.length} of {items.length} will be hidden.
            </div>
            <ul className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1" data-testid="admin-stale-list">
              {items.map((item) => {
                const keep = excluded.has(item.content_id);
                return (
                  <li
                    key={item.content_id}
                    className={`flex items-center gap-3 p-2.5 rounded border transition-colors ${
                      keep
                        ? 'bg-emerald-500/5 border-emerald-500/30'
                        : 'bg-rose-500/5 border-rose-500/30'
                    }`}
                    data-testid={`admin-stale-row-${item.content_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!keep}
                      onChange={() => toggleKeep(item.content_id)}
                      className="w-4 h-4 accent-rose-500 shrink-0"
                      aria-label={`Hide ${item.title}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium truncate">{item.title || '(no title)'}</div>
                      <div className="text-[11px] text-gray-400">
                        {item.type} · owner <code>{item.owner_id || '—'}</code> · age <span className="text-amber-300 font-semibold">{item.age_days ?? '?'}d</span>
                        {item.is_vault ? ' · [VAULT]' : ''}
                      </div>
                    </div>
                    {keep ? (
                      <Eye className="w-4 h-4 text-emerald-300 shrink-0" title="Will KEEP" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-rose-300 shrink-0" title="Will HIDE" />
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-gray-400">
                {target_ids.length} to hide · {items.length - target_ids.length} kept
              </div>
              <Button
                onClick={hide}
                disabled={busy || target_ids.length === 0}
                className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50"
                data-testid="admin-stale-apply"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Hide {target_ids.length} stale upload(s)
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StalePendingCleanup;
